import { config } from "@/lib/config";
import { invalidateQueries } from "@/lib/query-client";
import { useUIStore } from "@/stores/ui-store";
import { toast } from "@/utils/toast";
import { useAuth } from "@/lib/auth";
import { useCallback } from "react";

export function useAuthFetch() {
  const { getToken } = useAuth();
  const currentTenant = useUIStore((state) => state.currentTenant);
  const setCurrentTenant = useUIStore((state) => state.setCurrentTenant);

  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...((options.headers as Record<string, string>) || {}),
      };

      // Get token from Clerk
      try {
        const token = await getToken();
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        } else {
          console.warn("No auth token available");
        }
      } catch (error) {
        console.error("Failed to get auth token:", error);
      }

      // Use tenant from UI store (reactive to changes)
      if (currentTenant) {
        headers["X-Tenant-Slug"] = currentTenant;
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Check for tenant recovery mechanism
      const actualTenantSlug = response.headers.get("X-Actual-Tenant-Slug");
      if (actualTenantSlug && actualTenantSlug !== currentTenant) {
        setCurrentTenant(actualTenantSlug);

        // Show user-friendly notification
        toast.success(`Organization URL updated to: ${actualTenantSlug}`);
      }

      // Handle stale/invalid tenant slug - clear localStorage and reset state
      // This triggers the tenant recovery mechanism on next request
      if (response.status === 404 && currentTenant) {
        const data = await response
          .clone()
          .json()
          .catch(() => ({}));
        const isTenantNotFound =
          data.message?.includes("Tenant") &&
          data.message?.includes("not found");

        if (isTenantNotFound) {
          console.warn(
            `Tenant '${currentTenant}' not found, clearing stale tenant data and re-initializing`,
          );
          localStorage.removeItem("calendar_admin_tenant");
          // Full reload to re-trigger tenant initialization in App.tsx
          window.location.reload();
        }
      }

      return response;
    },
    [getToken, currentTenant, setCurrentTenant],
  );

  return authFetch;
}

// Custom hook for tenant switching that handles query invalidation
export function useTenantSwitch() {
  const authFetch = useAuthFetch();
  const { setCurrentTenant } = useUIStore((state) => ({
    setCurrentTenant: state.setCurrentTenant,
  }));

  const switchTenant = useCallback(
    async (tenantSlug: string) => {
      try {
        const response = await authFetch(
          `${config.apiBaseUrl}/user/tenants/switch`,
          {
            method: "POST",
            body: JSON.stringify({ tenantSlug }),
          },
        );

        if (response.ok) {
          const data = await response.json();

          // Update localStorage for persistence
          localStorage.setItem(
            "calendar_admin_tenant",
            JSON.stringify({
              id: data.tenant.id,
              slug: data.tenant.slug,
              name: data.tenant.name,
            }),
          );

          // Update the store
          setCurrentTenant(tenantSlug);

          // Invalidate all tenant-specific queries to refresh data
          // Pass the new tenant slug to invalidate specific tenant data
          invalidateQueries.allTenantData(tenantSlug);

          return { success: true, tenant: data.tenant };
        } else {
          console.error("Failed to switch tenant:", response.status);
          return { success: false, error: "Failed to switch tenant" };
        }
      } catch (error) {
        console.error("Error switching tenant:", error);
        return { success: false, error: "Error switching tenant" };
      }
    },
    [authFetch, setCurrentTenant],
  );

  return { switchTenant };
}
